import { useEffect, useState, useCallback, useMemo } from "react";
import { api, API, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Download, IndianRupee, RotateCcw, Zap, FileText, Check, X, Landmark, Loader2, AlertTriangle, Search } from "lucide-react";
import { toast } from "sonner";

const REFUND_REASONS = [
  "Admission Withdrawn / Cancelled",
  "Duplicate Payment",
  "Wrong Fee Category",
  "Technical Glitch / Double Charge",
  "Other",
];
const REFUND_TYPE_GATE = 25000; // amounts >= this require typing REFUND

const STATUS_STYLE = {
  success: "bg-emerald-100 text-emerald-800",
  initiated: "bg-amber-100 text-amber-800",
  failed: "bg-red-100 text-red-800",
  refunded: "bg-slate-100 text-slate-600",
};

const OFFLINE_METHODS = ["NEFT / RTGS", "Bank Challan", "Cash Counter"];
const FEE_TYPES = [
  { v: "application_fee", label: "Application Fee" },
  { v: "seat_token", label: "Seat Acceptance" },
  { v: "tuition", label: "Semester 1 Tuition" },
  { v: "hostel", label: "Hostel Fee" },
];
const FEE_LABEL = Object.fromEntries(FEE_TYPES.map((f) => [f.v, f.label]));
// Standard institution fee rules — pre-GST base defaults for the offline form.
const FEE_BASE = { application_fee: 1500, seat_token: 50000, tuition: 85000, hostel: 40000 };
const GST_RATE = 0.18;
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function Payments() {
  const [payments, setPayments] = useState([]);
  const [apps, setApps] = useState([]);
  const [allApps, setAllApps] = useState([]);
  const [queue, setQueue] = useState([]);
  const [pending, setPending] = useState(0);
  const [simOpen, setSimOpen] = useState(false);
  const [offOpen, setOffOpen] = useState(false);
  const [tab, setTab] = useState("ledger");
  const [refundFor, setRefundFor] = useState(null);

  const fetchAll = useCallback(() => {
    api.get("/payments").then((r) => setPayments(r.data)).catch(() => {});
    api.get("/applications", { params: { stage: "doc_verified" } }).then((r) => setApps(r.data.applications || [])).catch(() => {});
    api.get("/applications").then((r) => setAllApps(r.data.applications || [])).catch(() => {});
    api.get("/payments/reconciliation-queue")
      .then((r) => { setQueue(r.data.items || []); setPending(r.data.pending || 0); })
      .catch(() => {});
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const downloadReceipt = async (p) => {
    try {
      const r = await api.get(`/payments/${p.id}/receipt`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `receipt_${p.txn_id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("GST receipt downloaded");
    } catch {
      toast.error("Receipt download failed");
    }
  };

  const totalCollected = payments.filter((p) => p.status === "success").reduce((s, p) => s + p.total_amount, 0);
  const totalGst = payments.filter((p) => p.status === "success").reduce((s, p) => s + p.gst_amount, 0);

  return (
    <div data-testid="payments-page" className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Payments & GST</h1>
          <p className="text-sm text-slate-500 mt-1">Idempotent ledger, GST receipts, and offline (NEFT / challan) reconciliation.</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={offOpen} onOpenChange={setOffOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="log-offline-button" className="w-full sm:w-auto">
                <Landmark className="w-4 h-4 mr-1.5" /> Log Offline Deposit
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Log an offline deposit</DialogTitle></DialogHeader>
              <OfflineSubmitForm apps={allApps} onDone={() => { setOffOpen(false); fetchAll(); setTab("offline"); }} />
            </DialogContent>
          </Dialog>
          <Dialog open={simOpen} onOpenChange={setSimOpen}>
            <DialogTrigger asChild>
              <Button data-testid="simulate-payment-button" className="bg-brand w-full sm:w-auto">
                <Zap className="w-4 h-4 mr-1.5" /> Simulate Payment
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Simulate Gateway Payment</DialogTitle></DialogHeader>
              <SimulateForm apps={apps} onDone={() => { setSimOpen(false); fetchAll(); }} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="ledger" data-testid="tab-ledger">Transaction Ledger</TabsTrigger>
          <TabsTrigger value="offline" data-testid="tab-offline" className="gap-2">
            Offline Verification Queue
            {pending > 0 && (
              <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[11px] font-semibold">
                Pending Approvals ({pending})
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ledger" className="mt-4 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="p-5 border-slate-200" data-testid="payments-total-card">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Total Collected</div>
              <div className="font-mono text-2xl font-bold text-slate-900 flex items-center">
                <IndianRupee className="w-5 h-5" />{totalCollected.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
              </div>
            </Card>
            <Card className="p-5 border-slate-200" data-testid="payments-gst-card">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">GST Collected (18%)</div>
              <div className="font-mono text-2xl font-bold text-slate-900 flex items-center">
                <IndianRupee className="w-5 h-5" />{totalGst.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
              </div>
            </Card>
            <Card className="p-5 border-slate-200" data-testid="payments-count-card">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Transactions</div>
              <div className="font-mono text-2xl font-bold text-slate-900">{payments.length}</div>
            </Card>
          </div>

          <Card className="border-slate-200" data-testid="payments-table-card">
            <div className="w-full overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    {["Txn ID", "Student", "Course", "Fee Type", "Base + GST", "Total", "Method", "Status", "Actions"].map((h) => (
                      <TableHead key={h} className="text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.length === 0 && (
                    <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-400" data-testid="payments-empty-state">No transactions yet.</TableCell></TableRow>
                  )}
                  {payments.map((p, i) => (
                    <TableRow key={p.id} data-testid={`payment-row-${i}`}>
                      <TableCell className="font-mono text-xs text-slate-600">{p.txn_id}</TableCell>
                      <TableCell className="font-medium text-slate-800">{p.applicant_name}</TableCell>
                      <TableCell className="text-sm">{p.course}</TableCell>
                      <TableCell className="text-sm text-slate-600">{(p.fee_type || "").replace(/_/g, " ")}</TableCell>
                      <TableCell className="font-mono text-xs">
                        ₹{p.base_amount.toLocaleString("en-IN")} + ₹{p.gst_amount.toLocaleString("en-IN")}
                      </TableCell>
                      <TableCell className="font-mono font-semibold">₹{p.total_amount.toLocaleString("en-IN")}</TableCell>
                      <TableCell className="text-xs text-slate-500">{p.method} · {p.gateway}</TableCell>
                      <TableCell><Badge className={STATUS_STYLE[p.status]}>{p.status}</Badge></TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" data-testid={`download-receipt-${i}`} aria-label="Download receipt"
                            onClick={() => downloadReceipt(p)}>
                            <Download className="w-4 h-4 text-slate-500" />
                          </Button>
                          {p.status === "success" && (
                            <Button variant="ghost" size="icon" data-testid={`refund-payment-${i}`} aria-label="Refund"
                              onClick={() => setRefundFor(p)}>
                              <RotateCcw className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="offline" className="mt-4">
          <OfflineQueue items={queue} onChange={fetchAll} />
        </TabsContent>
      </Tabs>

      <RefundDialog
        payment={refundFor}
        onClose={() => setRefundFor(null)}
        onDone={() => { setRefundFor(null); fetchAll(); }}
      />
    </div>
  );
}

function RefundDialog({ payment, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setReason(""); setNotes(""); setTyped(""); }, [payment?.id]);

  if (!payment) return null;

  const amount = payment.total_amount || 0;
  const needsType = amount >= REFUND_TYPE_GATE;
  const notesRequired = reason === "Other";
  const canConfirm =
    !!reason &&
    (!notesRequired || notes.trim().length > 0) &&
    (!needsType || typed.trim().toUpperCase() === "REFUND") &&
    !busy;

  const submit = async () => {
    if (!canConfirm) return;
    setBusy(true);
    try {
      const r = await api.post(`/payments/${payment.id}/refund`, { reason, notes: notes.trim() || undefined });
      toast.success(
        r.data?.milestone_state === "refunded"
          ? "Refund processed — fee milestone rolled back"
          : "Refund processed",
      );
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Refund failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!payment} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="refund-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5" /> Confirm Payment Refund
          </DialogTitle>
        </DialogHeader>

        <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-slate-500">Student</span><span className="font-semibold text-slate-800">{payment.applicant_name}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Transaction ID</span><span className="font-mono text-xs text-slate-700">{payment.txn_id}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Fee Type</span><span className="text-slate-700">{(payment.fee_type || "").replace(/_/g, " ")}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Refund Amount</span><span className="font-mono font-bold text-red-700">₹{amount.toLocaleString("en-IN")}</span></div>
        </div>

        <div className="space-y-3">
          <div>
            <div className="text-sm font-medium mb-1.5">Reason <span className="text-red-600">*</span></div>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="refund-reason-select"><SelectValue placeholder="Select a refund reason" /></SelectTrigger>
              <SelectContent>
                {REFUND_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-sm font-medium mb-1.5">
              Additional notes {notesRequired ? <span className="text-red-600">*</span> : <span className="text-slate-400 font-normal">(optional)</span>}
            </div>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder={notesRequired ? "Required when reason is 'Other'" : "Context for the audit trail"}
              data-testid="refund-notes" />
          </div>
          {needsType && (
            <div>
              <div className="text-sm font-medium mb-1.5">
                Type <span className="font-mono font-bold">REFUND</span> to confirm this high-value refund
              </div>
              <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="REFUND"
                data-testid="refund-type-gate" />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="bg-red-600 hover:bg-red-700" onClick={submit} disabled={!canConfirm}
            data-testid="refund-confirm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm & Process Refund"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OfflineQueue({ items, onChange }) {
  const [rejectFor, setRejectFor] = useState(null);
  const [reason, setReason] = useState("");
  const [busyId, setBusyId] = useState("");

  const approve = async (tx) => {
    setBusyId(tx.id);
    try {
      const r = await api.post(`/payments/reconciliation/${tx.id}/review`, { status: "approved" });
      toast.success(`Approved — ledger receipt ${r.data.txn_id}`);
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Approval failed");
    } finally {
      setBusyId("");
    }
  };

  const submitReject = async () => {
    if (!reason.trim()) { toast.error("A rejection reason is required"); return; }
    setBusyId(rejectFor.id);
    try {
      const r = await api.post(`/payments/reconciliation/${rejectFor.id}/review`, {
        status: "rejected", rejection_reason: reason.trim(),
      });
      toast.success(r.data.fee_reminder_task ? "Rejected — follow-up task created for the counselor" : "Rejected");
      setRejectFor(null); setReason("");
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Rejection failed");
    } finally {
      setBusyId("");
    }
  };

  const pendingItems = items.filter((t) => t.status === "pending_approval");

  return (
    <Card className="border-slate-200" data-testid="offline-queue-card">
      <div className="w-full overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              {["Date", "Student & Course", "Fee Type", "UTR / Ref", "Amount (Total)", "Proof Slip", "Actions"].map((h) => (
                <TableHead key={h} className="text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {pendingItems.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400" data-testid="offline-empty-state">
                No offline deposits waiting for verification.
              </TableCell></TableRow>
            )}
            {pendingItems.map((t, i) => (
              <TableRow key={t.id} data-testid={`offline-row-${i}`}>
                <TableCell className="text-sm text-slate-600 font-mono">{t.deposit_date}</TableCell>
                <TableCell>
                  <div className="font-medium text-slate-800">{t.student_name}</div>
                  <div className="text-xs text-slate-400">{t.course}</div>
                </TableCell>
                <TableCell className="text-sm text-slate-600">{FEE_LABEL[t.fee_type] || t.fee_type}</TableCell>
                <TableCell className="font-mono text-xs text-slate-600">{t.utr_ref}<div className="text-[11px] text-slate-400">{t.method}</div></TableCell>
                <TableCell className="font-mono font-semibold">{inr(t.amount_total)}</TableCell>
                <TableCell>
                  {t.proof_file_url ? (
                    <Button variant="outline" size="sm" className="h-7" data-testid={`offline-proof-${i}`}
                      onClick={() => window.open(`${API}/payments/reconciliation/${t.id}/proof`, "_blank")}>
                      <FileText className="w-3.5 h-3.5 mr-1" /> View
                    </Button>
                  ) : <span className="text-xs text-slate-400">None</span>}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1.5">
                    <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700" data-testid={`offline-approve-${i}`}
                      disabled={busyId === t.id} onClick={() => approve(t)}>
                      {busyId === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Check className="w-3.5 h-3.5 mr-1" /> Verify &amp; Approve</>}
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 border-red-200 text-red-600 hover:bg-red-50" data-testid={`offline-reject-${i}`}
                      disabled={busyId === t.id} onClick={() => { setRejectFor(t); setReason(""); }}>
                      <X className="w-3.5 h-3.5 mr-1" /> Reject
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!rejectFor} onOpenChange={(o) => { if (!o) setRejectFor(null); }}>
        <DialogContent data-testid="offline-reject-modal">
          <DialogHeader><DialogTitle>Reject offline deposit</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">
            {rejectFor?.student_name} · {FEE_LABEL[rejectFor?.fee_type] || rejectFor?.fee_type} · {inr(rejectFor?.amount_total)}
          </p>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. UTR not matching the bank credit statement" data-testid="offline-reject-reason" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectFor(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={submitReject}
              disabled={busyId === rejectFor?.id} data-testid="offline-reject-submit">
              Reject &amp; notify counselor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function ApplicantCombobox({ apps, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = apps.find((a) => a.id === value);

  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = !s ? apps : apps.filter((a) =>
      [a.applicant_name, a.email, a.mobile, a.id].filter(Boolean)
        .some((x) => String(x).toLowerCase().includes(s)));
    return list.slice(0, 30);
  }, [apps, q]);

  if (selected) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border border-slate-300 bg-slate-50 px-3 py-2"
        data-testid="offline-app-selected">
        <div className="min-w-0">
          <div className="font-semibold text-slate-800 truncate">{selected.applicant_name}</div>
          <div className="text-xs text-slate-500 truncate">
            {selected.course}{selected.specialization ? ` · ${selected.specialization}` : ""} · {selected.id}
          </div>
        </div>
        <button type="button" aria-label="Change applicant" data-testid="offline-app-clear"
          onClick={() => { onChange(""); setQ(""); }}
          className="shrink-0 h-7 w-7 rounded-md flex items-center justify-center text-slate-400 hover:bg-slate-200">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
      <Input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Search applicant by name, email, or ID..."
        className="pl-9"
        data-testid="offline-app-search"
      />
      {open && (
        <div className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg">
          {results.length === 0 ? (
            <div className="px-3 py-3 text-sm text-slate-400">No matching applicants</div>
          ) : results.map((a) => (
            <button key={a.id} type="button" data-testid={`offline-app-opt-${a.id}`}
              onMouseDown={(e) => { e.preventDefault(); onChange(a.id); setOpen(false); setQ(""); }}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-800 truncate">{a.applicant_name}</span>
                <span className="shrink-0 text-[11px] rounded bg-slate-100 text-slate-600 px-1.5 py-0.5">
                  {a.course}{a.specialization ? ` · ${a.specialization}` : ""}
                </span>
              </div>
              <div className="text-xs text-slate-400 truncate">
                {a.id}{a.email ? ` · ${a.email}` : ""}{a.mobile ? ` · ${a.mobile}` : ""}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function OfflineSubmitForm({ apps, onDone }) {
  const [appId, setAppId] = useState("");
  const [feeType, setFeeType] = useState("seat_token");
  const [method, setMethod] = useState("NEFT / RTGS");
  const [utr, setUtr] = useState("");
  const [depositDate, setDepositDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState(String(FEE_BASE.seat_token));
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const pickFee = (v) => { setFeeType(v); setAmount(String(FEE_BASE[v] ?? "")); };
  const base = Number(amount) || 0;
  const gst = Math.round(base * GST_RATE * 100) / 100;
  const total = Math.round((base + gst) * 100) / 100;

  const submit = async (e) => {
    e.preventDefault();
    if (!appId || !utr.trim() || !amount) { toast.error("Application, UTR and amount are required"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("application_id", appId);
      fd.append("fee_type", feeType);
      fd.append("method", method);
      fd.append("utr_ref", utr.trim());
      fd.append("deposit_date", depositDate);
      fd.append("amount_base", String(amount));
      if (file) fd.append("proof", file);
      await api.post("/payments/offline-transaction", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logged — awaiting verification");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not log deposit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="offline-submit-form">
      <div>
        <div className="text-sm font-medium mb-1.5">Application</div>
        <ApplicantCombobox apps={apps} value={appId} onChange={setAppId} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-sm font-medium mb-1.5">Fee type</div>
          <Select value={feeType} onValueChange={pickFee}>
            <SelectTrigger data-testid="offline-fee-type"><SelectValue /></SelectTrigger>
            <SelectContent>{FEE_TYPES.map((f) => <SelectItem key={f.v} value={f.v}>{f.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-sm font-medium mb-1.5">Method</div>
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger data-testid="offline-method"><SelectValue /></SelectTrigger>
            <SelectContent>{OFFLINE_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-sm font-medium mb-1.5">UTR / Reference</div>
          <Input value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="UTR9928103948" data-testid="offline-utr" />
        </div>
        <div>
          <div className="text-sm font-medium mb-1.5">Deposit date</div>
          <input type="date" value={depositDate} onChange={(e) => setDepositDate(e.target.value)}
            className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm" data-testid="offline-date" />
        </div>
      </div>
      <div>
        <div className="text-sm font-medium mb-1.5">Amount (base, pre-GST) — editable for partial payments / scholarships</div>
        <Input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="50000" data-testid="offline-amount" />
        <div className="mt-1.5">
          <span className="inline-flex flex-wrap items-center gap-1 rounded-md bg-slate-100 px-2 py-1 text-xs text-slate-600"
            data-testid="offline-total-pill">
            Base: <b className="text-slate-800">{inr(base)}</b> + 18% GST (<b className="text-slate-800">{inr(gst)}</b>) =
            <b className="text-slate-900">{inr(total)}</b> Total Liability
          </span>
        </div>
      </div>
      <div>
        <div className="text-sm font-medium mb-1.5">Proof slip (PDF / JPG / PNG)</div>
        <input type="file" accept=".pdf,image/jpeg,image/png" onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="text-sm" data-testid="offline-proof-file" />
      </div>
      <Button type="submit" disabled={busy} className="w-full bg-brand">
        {busy ? "Submitting…" : "Submit for verification"}
      </Button>
    </form>
  );
}

function SimulateForm({ apps, onDone }) {
  const [appId, setAppId] = useState("");
  const [method, setMethod] = useState("UPI");
  const [feeType, setFeeType] = useState("application_fee");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/payments/simulate", { application_id: appId, method, fee_type: feeType });
      toast.success(`Payment approved: ${r.data.txn_id}`);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="simulate-payment-form">
      <div>
        <div className="text-sm font-medium mb-1.5">Application (doc verified)</div>
        <Select value={appId} onValueChange={setAppId}>
          <SelectTrigger data-testid="simulate-application-select"><SelectValue placeholder="Select application" /></SelectTrigger>
          <SelectContent>
            {apps.map((a) => (
              <SelectItem key={a.id} value={a.id}>{a.applicant_name} — {a.course}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <div className="text-sm font-medium mb-1.5">Fee Type</div>
        <Select value={feeType} onValueChange={setFeeType}>
          <SelectTrigger data-testid="simulate-fee-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="application_fee">Application Fee — ₹1,500</SelectItem>
            <SelectItem value="seat_token">Seat Acceptance — ₹50,000</SelectItem>
            <SelectItem value="tuition">Semester 1 Tuition — ₹85,000</SelectItem>
            <SelectItem value="hostel">Hostel Fee — ₹60,000</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <div className="text-sm font-medium mb-1.5">Method</div>
        <Select value={method} onValueChange={setMethod}>
          <SelectTrigger data-testid="simulate-method-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["UPI", "Card", "NetBanking"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Button data-testid="simulate-submit-button" type="submit" disabled={busy || !appId} className="w-full bg-emerald-600 hover:bg-emerald-700">
        {busy ? "Processing…" : "Approve Mock Payment (+18% GST)"}
      </Button>
    </form>
  );
}
